import React, { useState } from "react";
import { Box, MenuItem, TextField, Grid, Button, CircularProgress } from "@mui/material";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import httpClient from "../../lib/httpClient";
import { getUserData } from "../util/userUtils";

const EventType = {
  FLOOD: { value: "RIVFL", label: "Flood" },
  LANDSLIDE: { value: "landslide", label: "Landslide" },
};

type ReqParametersLayerProps = {
  polygonCountry: string | null;
  reqBtnDisabledFromMap: boolean;
  setRefreshTableFlag: (flag: boolean) => void;
  newEventReqPolygon: Partial<EventScopeRequest>;
  setNewEventReqPolygon: React.Dispatch<React.SetStateAction<Partial<EventScopeRequest>>>;
};

export const ReqParametersLayer: React.FC<ReqParametersLayerProps> = ({
  polygonCountry,
  reqBtnDisabledFromMap,
  setRefreshTableFlag,
  newEventReqPolygon,
  setNewEventReqPolygon,
}) => {
  const [eventType, setEventType] = useState<string>(EventType.FLOOD.value);
  const [eventDate, setEventDate] = useState<Date | null>(null);
  const [requestProcessing, setRequestProcessing] = useState<boolean>(false);

  const handleDateChange = (date: any, value?: string | null | undefined) => {
    setEventDate(date);
  };

  const submitRequest = async () => {
    setRequestProcessing(true);

    const loggedInUser = getUserData();
    await httpClient()
      .post("/eventScope/eventReq", {
        eventType,
        polygon: newEventReqPolygon?.aoiPolygonWKT,
        aoiPolygonBBWKT: newEventReqPolygon?.aoiPolygonBBWKT || "",
        aoiBB: newEventReqPolygon?.aoiBB || "",
        country: polygonCountry,
        startDate: eventDate,
        user: loggedInUser.email,
      })
      .then((response) => {
        setRequestProcessing(false);
        setRefreshTableFlag(true);
        setNewEventReqPolygon({ aoiPolygonWKT: "" }); // Reset event request polygon input
        setEventDate(null); // Reset the event date
        console.log("Request submitted successfully", response.data);
      })
      .catch((error) => {
        setRequestProcessing(false);
        console.error("Error submitting request", error);
      });
  };

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item>
        <TextField
          style={{ width: 200 }}
          variant="outlined"
          select
          label="Event Type"
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
        >
          <MenuItem value={EventType.FLOOD.value}>{EventType.FLOOD.label}</MenuItem>
          <MenuItem value={EventType.LANDSLIDE.value} disabled>
            {EventType.LANDSLIDE.label}
          </MenuItem>
        </TextField>
      </Grid>

      <Grid item>
        <TextField
          label="Area of interest - WKT"
          variant="outlined"
          value={newEventReqPolygon?.aoiPolygonWKT}
          InputLabelProps={{
            shrink:
              newEventReqPolygon?.aoiPolygonWKT && newEventReqPolygon?.aoiPolygonWKT.length > 0
                ? true
                : false, // Forces the label to stay at the top
          }}
          onChange={(e) =>
            setNewEventReqPolygon((prev: Partial<EventScopeRequest>) => ({
              ...prev,
              aoiPolygonWKT: e.target.value,
            }))
          }
        />
      </Grid>

      <Grid item>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            inputVariant="outlined"
            format="MM/dd/yyyy"
            minDate={new Date("2014-01-01")} // 👈 Disables dates before 2014
            maxDate={new Date()}
            label="Event date"
            value={eventDate}
            onChange={handleDateChange}
            openTo="year" // Start with the year selection
            views={["year", "month", "date"]} // Define the order of views
          />
        </MuiPickersUtilsProvider>
      </Grid>

      <Grid item>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          disabled={
            requestProcessing ||
            reqBtnDisabledFromMap ||
            !polygonCountry ||
            newEventReqPolygon?.aoiPolygonWKT?.length === 0 ||
            !eventDate
          }
          onClick={() => submitRequest()}
        >
          {requestProcessing && (
            <Box mr={1}>
              <CircularProgress size={12}></CircularProgress>
            </Box>
          )}
          Request analysis
        </Button>
      </Grid>
    </Grid>
  );
};
